<script setup>
// SiteUp — Camadas 1 e 2 do guia do Modo Foco: card de boas-vindas
// (uma ideia) + tour opcional. Laterais: uma troca só, sem loop. Nunca prende.
import {
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  inject,
} from 'vue';
import { useI18n } from 'vue-i18n';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import FocusSpotlight from './FocusSpotlight.vue';
import {
  useFocusSpotlight,
  findSpotTarget,
} from 'dashboard/composables/useFocusSpotlight';
import { useFocusMode } from 'dashboard/composables/useFocusMode';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';

const TOUR_STEPS = [
  {
    id: 'exit',
    selector: '[data-testid="focus-mode-exit"]',
    openMenu: false,
  },
  {
    id: 'arrow',
    selector: '[data-testid="focus-mode-menu-toggle"]',
    openMenu: false,
  },
  {
    id: 'modes',
    selector: '[data-testid="focus-mode-menu"]',
    openMenu: true,
  },
  {
    id: 'edges',
    selectors: [
      '[data-testid="conversation-workbench-list-gutter"]',
      '[data-testid="conversation-workbench-right-gutter"]',
    ],
    openMenu: false,
  },
  {
    id: 'context',
    selector: '[data-testid="conversation-list-shell"]',
    openMenu: false,
    showMenuMock: true,
  },
  {
    id: 'railAdd',
    selector: '[data-testid="shortcut-rail-add"]',
    openMenu: false,
  },
  {
    id: 'railPin',
    selector: '[data-testid="conversation-workbench-rail"]',
    openMenu: false,
  },
  {
    id: 'customize',
    selector: '[data-testid="focus-mode-customize"]',
    openMenu: true,
  },
];

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const getAllConversations = useMapGetter('getAllConversations');
const { shouldShowWelcome, markWelcomeSeen, markTourCompleted, saveTourStep } =
  useFocusMode();
const host = inject('focusModeGuideHost', null);
const {
  spotlightStyle,
  setSelector,
  measure,
  attach,
  detach,
  scheduleMeasure,
} = useFocusSpotlight();

const showWelcome = ref(false);
const showTour = ref(false);
const stepIndex = ref(0);
const cardRef = ref(null);
const balloonRef = ref(null);

let previousFocus = null;
let ignoreOutsideClick = false;
let onDocKeydown = null;
let onDocClick = null;
let cycleTimer = null;

function stepTargets(step) {
  if (!step) return [];
  const list = step.selectors || (step.selector ? [step.selector] : []);
  return list.filter(selector => findSpotTarget(selector));
}

function stepIsReachable(step) {
  if (!step) return false;
  if (step.openMenu) return true;
  return stepTargets(step).length > 0;
}

function nextValidIndex(from, dir) {
  let index = from + dir;
  while (index >= 0 && index < TOUR_STEPS.length) {
    if (stepIsReachable(TOUR_STEPS[index])) return index;
    index += dir;
  }
  return -1;
}

function stopCycle() {
  if (!cycleTimer) return;
  window.clearTimeout(cycleTimer);
  window.clearInterval(cycleTimer);
  cycleTimer = null;
}

function waitForTarget(selector, attempts = 12) {
  return new Promise(resolve => {
    const tick = left => {
      if (findSpotTarget(selector) || left <= 0) {
        resolve(Boolean(findSpotTarget(selector)));
        return;
      }
      window.setTimeout(() => tick(left - 1), 50);
    };
    tick(attempts);
  });
}

const currentStep = computed(() => TOUR_STEPS[stepIndex.value] || null);
const isLastStep = computed(() => {
  const next = nextValidIndex(stepIndex.value, 1);
  return next < 0;
});

const stepTitle = computed(() => {
  const step = currentStep.value;
  if (!step) return '';
  if (step.id === 'exit') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_1_TITLE');
  if (step.id === 'arrow') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_2_TITLE');
  if (step.id === 'modes') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_3_TITLE');
  if (step.id === 'edges') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_4_TITLE');
  if (step.id === 'context') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_5_TITLE');
  if (step.id === 'railAdd') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_6_TITLE');
  if (step.id === 'railPin') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_7_TITLE');
  return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_8_TITLE');
});

const stepBody = computed(() => {
  const step = currentStep.value;
  if (!step) return '';
  if (step.id === 'exit') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_1_BODY');
  if (step.id === 'arrow') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_2_BODY');
  if (step.id === 'modes') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_3_BODY');
  if (step.id === 'edges') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_4_BODY');
  if (step.id === 'context') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_5_BODY');
  if (step.id === 'railAdd') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_6_BODY');
  if (step.id === 'railPin') return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_7_BODY');
  return t('SIDEBAR.FOCUS_MODE.TOUR_STEP_8_BODY');
});

const stepTips = computed(() => {
  const step = currentStep.value;
  if (step?.id === 'railAdd') {
    return [
      t('SIDEBAR.FOCUS_MODE.TOUR_RAIL_ADD_TIP_1'),
      t('SIDEBAR.FOCUS_MODE.TOUR_RAIL_ADD_TIP_2'),
      t('SIDEBAR.FOCUS_MODE.TOUR_RAIL_ADD_TIP_3'),
    ];
  }
  if (step?.id === 'railPin') {
    return [t('SIDEBAR.FOCUS_MODE.TOUR_RAIL_PIN_TIP')];
  }
  return [];
});

const mockMenuItems = computed(() => [
  t('CONVERSATION.CARD_CONTEXT_MENU.PIN_TO_TOP'),
  t('CONVERSATION.CARD_CONTEXT_MENU.PIN_TO_DOCK'),
  t('CONVERSATION.CARD_CONTEXT_MENU.MARK_AS_UNREAD'),
  t('CONVERSATION.CARD_CONTEXT_MENU.RESOLVED'),
]);

const stepLabel = computed(
  () => `${stepIndex.value + 1} / ${TOUR_STEPS.length}`
);

const isGuideOpen = computed(() => showWelcome.value || showTour.value);

const firstValidIndex = () =>
  TOUR_STEPS.findIndex(step => stepIsReachable(step));

const restoreFocus = () => {
  if (previousFocus && typeof previousFocus.focus === 'function') {
    previousFocus.focus();
  }
  previousFocus = null;
};

const detachDoc = () => {
  if (typeof document === 'undefined') return;
  document.removeEventListener('keydown', onDocKeydown);
  document.removeEventListener('click', onDocClick);
};

const focusCard = () => {
  nextTick(() => {
    const root = showTour.value ? balloonRef.value : cardRef.value;
    root?.querySelector('button')?.focus();
  });
};

const syncSpot = selector => {
  setSelector(selector);
  return measure();
};

function finishTour() {
  stopCycle();
  showTour.value = false;
  host?.closeMenu?.();
  detach();
  setSelector('');
  markTourCompleted();
  detachDoc();
  restoreFocus();
}

const paintTargets = targets => {
  const paint = selector => {
    attach([selector, '[data-testid="focus-mode-cluster"]']);
    syncSpot(selector);
  };
  paint(targets[0]);
  if (targets.length < 2) return;
  cycleTimer = window.setTimeout(() => {
    cycleTimer = null;
    paint(targets[1]);
  }, 1600);
};

const applyStep = index => {
  stopCycle();
  const step = TOUR_STEPS[index];
  if (!step) {
    finishTour();
    return;
  }
  if (step.openMenu) host?.openMenu?.();
  else host?.closeMenu?.();
  stepIndex.value = index;
  saveTourStep(index);
  nextTick(async () => {
    if (step.openMenu && step.selector) {
      await waitForTarget(step.selector, 16);
    }
    const targets = stepTargets(step);
    if (!targets.length) {
      const fallback = nextValidIndex(index, 1);
      if (fallback < 0) {
        finishTour();
        return;
      }
      applyStep(fallback);
      return;
    }
    paintTargets(targets);
    focusCard();
    nextTick(() => syncSpot(targets[0]));
  });
};

function dismissWelcome() {
  showWelcome.value = false;
  detach();
  setSelector('');
  markWelcomeSeen();
  detachDoc();
  restoreFocus();
}

function dismissTour() {
  stopCycle();
  showTour.value = false;
  host?.closeMenu?.();
  detach();
  setSelector('');
  markWelcomeSeen();
  saveTourStep(stepIndex.value);
  detachDoc();
  restoreFocus();
}

function closeGuide() {
  if (showTour.value) {
    dismissTour();
    return;
  }
  if (showWelcome.value) dismissWelcome();
}

onDocKeydown = event => {
  if (!isGuideOpen.value || event.key !== 'Escape') return;
  event.preventDefault();
  closeGuide();
};

onDocClick = event => {
  if (!isGuideOpen.value || ignoreOutsideClick) return;
  const card = showTour.value ? balloonRef.value : cardRef.value;
  if (card?.contains(event.target)) return;
  const selectors = showTour.value
    ? stepTargets(currentStep.value)
    : ['[data-testid="focus-mode-cluster"]'];
  const insideTarget = selectors.some(selector =>
    document.querySelector(selector)?.contains(event.target)
  );
  if (insideTarget) return;
  closeGuide();
};

const attachDoc = () => {
  if (typeof document === 'undefined') return;
  detachDoc();
  document.addEventListener('keydown', onDocKeydown);
  document.addEventListener('click', onDocClick);
};

const startWelcome = () => {
  previousFocus = document.activeElement;
  showWelcome.value = true;
  ignoreOutsideClick = true;
  attach(['[data-testid="focus-mode-cluster"]']);
  syncSpot('[data-testid="focus-mode-cluster"]');
  attachDoc();
  focusCard();
  if (typeof window === 'undefined') return;
  window.setTimeout(() => {
    ignoreOutsideClick = false;
    scheduleMeasure();
    syncSpot('[data-testid="focus-mode-cluster"]');
  }, 80);
};

const ensureConversationChrome = async () => {
  const hasList = () =>
    findSpotTarget('[data-testid="conversation-list-shell"]');
  const hasRail = () =>
    findSpotTarget('[data-testid="conversation-workbench-rail"]');
  if (hasList() && hasRail()) return;

  let first = getAllConversations.value?.[0];
  if (!first?.id && !hasList()) {
    await router.replace(accountScopedRoute('home'));
    await waitForTarget('[data-testid="conversation-list-shell"]');
    first = getAllConversations.value?.[0];
  }
  if (!first?.id) return;
  await router.replace(
    accountScopedRoute('inbox_conversation', {
      conversation_id: first.id,
    })
  );
  await nextTick();
  await waitForTarget('[data-testid="conversation-workbench-rail"]');
};

const startTour = async () => {
  showWelcome.value = false;
  markWelcomeSeen();
  previousFocus = previousFocus || document.activeElement;
  await ensureConversationChrome();
  const start = firstValidIndex();
  if (start < 0) {
    markWelcomeSeen();
    return;
  }
  showTour.value = true;
  ignoreOutsideClick = true;
  applyStep(start);
  attachDoc();
  if (typeof window === 'undefined') return;
  window.setTimeout(() => {
    ignoreOutsideClick = false;
  }, 80);
};

const goNext = () => {
  const next = nextValidIndex(stepIndex.value, 1);
  if (next < 0) {
    finishTour();
    return;
  }
  applyStep(next);
};

const goBack = () => {
  const prev = nextValidIndex(stepIndex.value, -1);
  if (prev < 0) return;
  applyStep(prev);
};

onMounted(() => {
  if (shouldShowWelcome.value) startWelcome();
});

onBeforeUnmount(() => {
  stopCycle();
  detachDoc();
  detach();
});

defineExpose({ startTour });
</script>

<template>
  <TeleportWithDirection to="body">
    <template v-if="isGuideOpen">
      <FocusSpotlight :style-value="spotlightStyle" />

      <div
        v-if="showWelcome"
        ref="cardRef"
        role="dialog"
        aria-modal="true"
        aria-labelledby="focus-mode-welcome-title"
        class="fixed left-1/2 top-1/2 z-[80] w-72 max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-visible rounded-2xl border border-n-weak bg-n-solid-2 p-4 shadow-xl"
        data-testid="focus-mode-welcome"
        @click.stop
      >
        <p
          id="focus-mode-welcome-title"
          class="m-0 mb-1 text-base font-semibold text-n-slate-12"
        >
          {{ t('SIDEBAR.FOCUS_MODE.WELCOME_TITLE') }}
        </p>
        <p class="m-0 mb-3 text-sm leading-relaxed text-n-slate-11">
          {{ t('SIDEBAR.FOCUS_MODE.WELCOME_BODY') }}
        </p>
        <div class="flex flex-col items-stretch gap-2">
          <button
            type="button"
            class="min-h-9 w-full rounded-full bg-n-slate-12 px-4 py-2 text-sm font-semibold leading-snug text-n-background hover:opacity-90"
            data-testid="focus-mode-welcome-tour"
            @click="startTour"
          >
            {{ t('SIDEBAR.FOCUS_MODE.WELCOME_TOUR') }}
          </button>
          <button
            type="button"
            class="min-h-9 w-full rounded-full px-3 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="focus-mode-welcome-later"
            @click="dismissWelcome"
          >
            {{ t('SIDEBAR.FOCUS_MODE.WELCOME_LATER') }}
          </button>
        </div>
      </div>

      <div
        v-else-if="showTour"
        ref="balloonRef"
        role="dialog"
        aria-modal="true"
        aria-labelledby="focus-mode-tour-title"
        class="fixed left-1/2 top-1/2 z-[80] flex h-[min(22rem,calc(100vh-4rem))] w-72 max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-2xl border border-n-weak bg-n-solid-2 p-4 shadow-xl"
        data-testid="focus-mode-tour"
        :data-step="currentStep?.id"
        @click.stop
      >
        <p
          class="m-0 mb-1 text-[11px] font-semibold tabular-nums text-n-iris-11"
        >
          {{ stepLabel }}
        </p>
        <p
          id="focus-mode-tour-title"
          class="m-0 mb-1 text-sm font-semibold text-n-slate-12"
        >
          {{ stepTitle }}
        </p>
        <p class="m-0 mb-3 text-sm leading-relaxed text-n-slate-11">
          {{ stepBody }}
        </p>
        <ul
          v-if="currentStep?.showMenuMock"
          class="mb-3 list-none rounded-xl border border-n-weak bg-n-solid-1 p-1.5"
          aria-hidden="true"
        >
          <li
            v-for="label in mockMenuItems"
            :key="label"
            class="rounded-lg px-2 py-1.5 text-xs font-medium text-n-slate-12"
          >
            {{ label }}
          </li>
        </ul>
        <ul v-else-if="stepTips.length" class="mb-3 list-none space-y-1 p-0">
          <li
            v-for="label in stepTips"
            :key="label"
            class="text-sm leading-snug text-n-slate-11"
          >
            {{ label }}
          </li>
        </ul>
        <div class="mt-auto flex items-center justify-between gap-1">
          <button
            type="button"
            class="min-h-9 shrink-0 rounded-full px-3 text-xs font-semibold text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="focus-mode-tour-skip"
            @click="dismissTour"
          >
            {{ t('SIDEBAR.FOCUS_MODE.TOUR_SKIP') }}
          </button>
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="min-h-9 rounded-full px-3 text-xs font-semibold text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
              data-testid="focus-mode-tour-back"
              :disabled="nextValidIndex(stepIndex, -1) < 0"
              @click="goBack"
            >
              {{ t('SIDEBAR.FOCUS_MODE.TOUR_BACK') }}
            </button>
            <button
              type="button"
              class="min-h-9 rounded-full bg-n-slate-12 px-3 text-xs font-extrabold text-n-background hover:opacity-90"
              data-testid="focus-mode-tour-next"
              @click="goNext"
            >
              {{
                isLastStep
                  ? t('SIDEBAR.FOCUS_MODE.TOUR_DONE')
                  : t('SIDEBAR.FOCUS_MODE.TOUR_NEXT')
              }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </TeleportWithDirection>
</template>
